import { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { useSickTrainings } from '../../graphql/hooks/useUserDashboard';
import { useDeleteSickTracking } from '../../graphql/hooks/useAdminDashboard';
import { ConfirmModal } from '../core';

export function SickLeaveView() {
  const [sickCurrentPage, setSickCurrentPage] = useState(1);
  const [sickItemsPerPage, setSickItemsPerPage] = useState(10);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);

  const { data: sickTrackings } = useSickTrainings(sickItemsPerPage, sickCurrentPage);

  const sickTrackingData = sickTrackings?.getSickTrackings?.data ?? [];
  const paginator = sickTrackings?.getSickTrackings.paginatorInfo;

  const { mutate: deleteSickTracking } = useDeleteSickTracking();

  const handleDeleteClick = (id: string) => {
    setRequestToDelete(id);
    setDeleteModalOpen(true);
    setOpenDropdownId(null);
  };

  return (
    <div className="space-y-6">
      {/* Data Display */}
      {sickTrackingData?.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-[#6b6b6b] text-[16px]">
            Für den ausgewählten Monat sind noch keine Daten vorhanden
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[#6b6b6b] text-[14px]">
            <span>Zeige</span>
            <select
              value={sickItemsPerPage}
              onChange={(e) => {
                setSickItemsPerPage(Number(e.target.value));
                setSickCurrentPage(1);
              }}
              className="form-select px-2 py-1 border border-[#e0e0e0] rounded text-[#1c1c1c]"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>Einträge</span>
          </div>

          {/* Table Header */}
          <div className="grid grid-cols-4 gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]">
            <div>Name</div>
            <div>Beginn</div>
            <div>Ende</div>
            <div>Meldedatum</div>
          </div>

          {/* Table Rows */}
          {sickTrackingData?.map((entry) => (
            <div
              key={entry.id}
              className="grid grid-cols-4 gap-4 px-4 py-3 text-[16px] items-center hover:bg-[#f5f5f5] transition-colors"
            >
              <div className="text-[#1c1c1c]">{entry.user.full_name}</div>
              <div className="text-[#6b6b6b]">{entry.start_date}</div>
              <div className="text-[#6b6b6b]">{entry.end_date}</div>
              <div className="text-[#6b6b6b] flex justify-between items-center">
                <span>{entry.report_date}</span>

                <div className="relative">
                  <button
                    onClick={() =>
                      setOpenDropdownId(openDropdownId === entry.id ? null : entry.id)
                    }
                    className="p-1 hover:bg-[#f5f5f5] rounded-[6px] transition-colors"
                  >
                    <MoreVertical className="size-5 text-[#6b6b6b]" />
                  </button>

                  {openDropdownId === entry.id && (
                    <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[140px]">
                      <button
                        onClick={() => handleDeleteClick(entry.id)}
                        className="w-full px-4 py-2.5 text-left text-[14px] text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors flex items-center gap-2"
                      >
                        Löschen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between pt-4 border-t border-[#e0e0e0]">
        {/* Info */}
        <div className="text-[#6b6b6b] text-[14px]">
          Zeige{' '}
          {Math.min(
            ((paginator?.currentPage ?? 1) - 1) * sickItemsPerPage + 1,
            paginator?.total ?? 0
          )}{' '}
          bis{' '}
          {Math.min(
            (paginator?.currentPage ?? 1) * sickItemsPerPage,
            paginator?.total ?? 0
          )}{' '}
          von {paginator?.total ?? 0} Einträgen
        </div>

        {/* Page Controls */}
        <div className="flex items-center gap-2">
          {/* Previous */}
          <button
            onClick={() => setSickCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={paginator?.currentPage === 1}
            className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Vorherige
          </button>

          {/* Page Numbers */}
          {(() => {
            const totalPages = paginator?.lastPage ?? 1;
            const pageNumbers = [];
            const maxVisiblePages = 5;

            let startPage = Math.max(
              1,
              (paginator?.currentPage ?? 1) - Math.floor(maxVisiblePages / 2)
            );
            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

            if (endPage - startPage < maxVisiblePages - 1) {
              startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
              pageNumbers.push(i);
            }

            return pageNumbers.map((number) => (
              <button
                key={number}
                onClick={() => setSickCurrentPage(number)}
                className={`px-3 py-1 text-[14px] rounded transition-colors ${
                  paginator?.currentPage === number
                    ? 'bg-[#0084c7] text-white'
                    : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
                }`}
              >
                {number}
              </button>
            ));
          })()}

          {/* Next */}
          <button
            onClick={() =>
              setSickCurrentPage((prev) => Math.min(paginator?.lastPage ?? prev, prev + 1))
            }
            disabled={paginator?.currentPage === paginator?.lastPage}
            className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Nächste
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Krankmeldung löschen"
        message="Möchten Sie den Eintrag wirklich löschen?"
        onConfirm={() => {
          if (requestToDelete) {
            deleteSickTracking({ id: requestToDelete });
            setDeleteModalOpen(false);
            setRequestToDelete(null);
          }
        }}
      />
    </div>
  );
}